import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  BookOpenCheck,
  Building2,
  ChevronDown,
  ClipboardList,
  FileStack,
  Globe2,
  Home,
  Info,
  LayoutDashboard,
  LogIn,
  LogOut,
  Menu,
  Network,
  Send,
  Settings2,
  ShieldCheck,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const API_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://api.certilink.tn";

const decodeToken = (token) => {
  try {
    if (!token) return null;
    const payload = token.split(".")[1];
    if (!payload) return null;
    const normalized = payload
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(payload.length / 4) * 4, "=");
    return JSON.parse(
      decodeURIComponent(
        atob(normalized)
          .split("")
          .map((character) =>
            `%${character.charCodeAt(0).toString(16).padStart(2, "0")}`
          )
          .join("")
      )
    );
  } catch (error) {
    console.error("Unable to decode authentication token:", error);
    return null;
  }
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [canManageThemes, setCanManageThemes] = useState(false);
  const [certificateTemplateMode, setCertificateTemplateMode] = useState(null);
  const profileRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { toggleLanguage, t, lang } = useLanguage();

  const tr = (key, fallback) => t?.ui?.navbar?.[key] || t?.[key] || fallback;

  const readSession = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setIsLoggedIn(false);
      setUser(null);
      setCanManageThemes(false);
      setCertificateTemplateMode(null);
      return;
    }

    const tokenData = decodeToken(token);
    let storedUser = null;
    try {
      storedUser = JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      storedUser = null;
    }

    const normalizedUser = {
      id: tokenData?.id || tokenData?._id || storedUser?.id || storedUser?._id,
      type: tokenData?.type || storedUser?.type || null,
      name: tokenData?.name || storedUser?.name || tr("user", "Utilisateur"),
      status: tokenData?.status || storedUser?.status || "Active",
      adminRole: tokenData?.adminRole || storedUser?.adminRole || null,
      isSuperAdmin: Boolean(
        tokenData?.isSuperAdmin ||
          storedUser?.isSuperAdmin ||
          tokenData?.adminRole === "SuperAdmin" ||
          storedUser?.adminRole === "SuperAdmin"
      ),
      isOrganizationAdmin: Boolean(
        tokenData?.isOrganizationAdmin ||
          storedUser?.isOrganizationAdmin ||
          tokenData?.adminRole === "OrganizationAdmin" ||
          storedUser?.adminRole === "OrganizationAdmin"
      ),
      organizationRef:
        tokenData?.organizationRef || storedUser?.organizationRef || null,
      organization:
        tokenData?.organization || storedUser?.organization || null,
      organizationName:
        tokenData?.organizationName ||
        storedUser?.organizationName ||
        tokenData?.organization?.name ||
        storedUser?.organization?.name ||
        "",
      organizationSlug:
        tokenData?.organizationSlug ||
        storedUser?.organizationSlug ||
        tokenData?.organization?.slug ||
        storedUser?.organization?.slug ||
        "",
      templatePolicy:
        tokenData?.templatePolicy ||
        storedUser?.templatePolicy ||
        tokenData?.organization?.templatePolicy ||
        storedUser?.organization?.templatePolicy ||
        null,
      permissions:
        tokenData?.permissions || storedUser?.permissions || {},
      canManageThemes: Boolean(
        tokenData?.canManageThemes || storedUser?.canManageThemes
      ),
    };

    setIsLoggedIn(true);
    setUser(normalizedUser);
    localStorage.setItem("user", JSON.stringify(normalizedUser));
  };

  useEffect(() => {
    readSession();
    const handleSessionChange = () => readSession();
    window.addEventListener("storage", handleSessionChange);
    window.addEventListener("loginStateChanged", handleSessionChange);
    window.addEventListener("subscriptionRenewed", handleSessionChange);
    return () => {
      window.removeEventListener("storage", handleSessionChange);
      window.removeEventListener("loginStateChanged", handleSessionChange);
      window.removeEventListener("subscriptionRenewed", handleSessionChange);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return undefined;

    let cancelled = false;
    const refreshSession = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.user || cancelled) return;
        localStorage.setItem("user", JSON.stringify(data.user));
        setUser(data.user);
        setIsLoggedIn(true);
      } catch {
        // La session locale reste disponible en cas de panne réseau temporaire.
      }
    };

    refreshSession();
    window.addEventListener("focus", refreshSession);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", refreshSession);
    };
  }, [location.pathname]);

  useEffect(() => {
    const token = localStorage.getItem("token");

    const organizationAdmin =
      user?.isOrganizationAdmin === true ||
      user?.adminRole === "OrganizationAdmin" ||
      user?.role === "OrganizationAdmin";

    const superAdmin =
      user?.isSuperAdmin === true ||
      user?.adminRole === "SuperAdmin" ||
      user?.role === "SuperAdmin";

    const canCheck =
      isLoggedIn &&
      user?.status === "Active" &&
      user?.type === "Admin" &&
      organizationAdmin &&
      !superAdmin;

    if (!token || !canCheck) {
      setCertificateTemplateMode(null);
      return undefined;
    }

    let cancelled = false;

    const loadCertificateTemplatePolicy = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/organizations/current/template-policy`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await response.json().catch(() => ({}));

        if (cancelled) return;

        setCertificateTemplateMode(
          response.ok && data?.mode === "single"
            ? "single"
            : null
        );
      } catch {
        if (!cancelled) {
          setCertificateTemplateMode(null);
        }
      }
    };

    loadCertificateTemplatePolicy();

    window.addEventListener("focus", loadCertificateTemplatePolicy);
    window.addEventListener(
      "certificateTemplateChanged",
      loadCertificateTemplatePolicy
    );

    return () => {
      cancelled = true;
      window.removeEventListener("focus", loadCertificateTemplatePolicy);
      window.removeEventListener(
        "certificateTemplateChanged",
        loadCertificateTemplatePolicy
      );
    };
  }, [
    isLoggedIn,
    user?.type,
    user?.status,
    user?.isOrganizationAdmin,
    user?.isSuperAdmin,
    user?.adminRole,
    user?.role,
    location.pathname,
  ]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const canCheck =
      isLoggedIn &&
      user?.status === "Active" &&
      user?.type === "Client";

    if (!token || !canCheck) {
      setCanManageThemes(false);
      return undefined;
    }

    let cancelled = false;
    const loadThemeAccess = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/certifs/theme-management-access`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await response.json().catch(() => ({}));
        if (!cancelled) {
          setCanManageThemes(
            response.ok && Boolean(data.canManageThemes)
          );
        }
      } catch {
        if (!cancelled) setCanManageThemes(false);
      }
    };

    loadThemeAccess();
    window.addEventListener("focus", loadThemeAccess);
    window.addEventListener("themeManagementAccessChanged", loadThemeAccess);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", loadThemeAccess);
      window.removeEventListener("themeManagementAccessChanged", loadThemeAccess);
    };
  }, [
    isLoggedIn,
    user?.type,
    user?.status,
    location.pathname,
  ]);

  useEffect(() => {
    setIsOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const closeProfile = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };
    const closeWithEscape = (event) => {
      if (event.key === "Escape") {
        setProfileOpen(false);
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", closeProfile);
    document.addEventListener("keydown", closeWithEscape);
    return () => {
      document.removeEventListener("mousedown", closeProfile);
      document.removeEventListener("keydown", closeWithEscape);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setIsLoggedIn(false);
    setUser(null);
    setProfileOpen(false);
    setCanManageThemes(false);
    setCertificateTemplateMode(null);
    setIsOpen(false);
    window.dispatchEvent(new Event("loginStateChanged"));
    navigate("/", { replace: true });
  };

  const userType = user?.type;
  const isAdmin = isLoggedIn && userType === "Admin";

  const isSuperAdmin =
    isAdmin &&
    Boolean(
      user?.isSuperAdmin ||
        user?.adminRole === "SuperAdmin" ||
        user?.role === "SuperAdmin"
    );

  const isOrganizationAdmin =
    isAdmin &&
    !isSuperAdmin &&
    Boolean(
      user?.isOrganizationAdmin ||
        user?.adminRole === "OrganizationAdmin" ||
        user?.role === "OrganizationAdmin"
    );

  const canConfigureCertificateTemplate =
    isOrganizationAdmin &&
    user?.status === "Active" &&
    certificateTemplateMode === "single";

  const isActiveClient =
    isLoggedIn && userType === "Client" && user?.status === "Active";
  const userInitial = user?.name?.trim()?.charAt(0)?.toUpperCase() || "U";
  const permissions = user?.permissions || {};
  const canSeeParticipants =
    isActiveClient ||
    isSuperAdmin ||
    (isOrganizationAdmin && permissions.manageParticipants !== false);
  const canSeeClients =
    isSuperAdmin ||
    (isOrganizationAdmin && permissions.manageClients !== false);
  const canSeeRequests =
    isSuperAdmin ||
    (isOrganizationAdmin && permissions.manageOrganizationUsers !== false);

  const links = [
    { to: "/", label: t?.home || "Accueil", icon: Home, visible: true, end: true },
    { to: "/about", label: t?.about || "À propos", icon: Info, visible: true },
    {
      to: "/participants",
      label: t?.participants || "Participants",
      icon: Users,
      visible: canSeeParticipants,
    },
    {
      to: "/clients",
      label: t?.clients || "Clients",
      icon: Building2,
      visible: canSeeClients,
    },
    {
      to: "/requests/all",
      label: t?.requests || "Demandes",
      icon: ClipboardList,
      visible: canSeeRequests,
    },
    {
      to: "/admins",
      label: lang === "en" ? "Administrators" : "Administrateurs",
      icon: ShieldCheck,
      visible: isSuperAdmin,
    },
    {
      to: "/organizations",
      label: lang === "en" ? "Organizations" : "Organisations",
      icon: Network,
      visible: isSuperAdmin,
    },
  ].filter((item) => item.visible);

  const desktopLinkClass = ({ isActive }) =>
    `rounded-xl px-3 py-2 text-sm font-semibold transition ${
      isActive
        ? "bg-blue-50 text-blue-700 ring-1 ring-blue-100"
        : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
    }`;

  const mobileLinkClass = ({ isActive }) =>
    `flex items-center gap-3 rounded-2xl px-4 py-3.5 text-sm font-bold transition ${
      isActive
        ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
        : "border border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50"
    }`;

  const roleLabel = isSuperAdmin
    ? tr("superAdmin", "Super administrateur")
    : isOrganizationAdmin
      ? tr("organizationAdmin", "Administrateur d’organisation")
      : tr("client", "Client");

  const organizationName =
    user?.organization?.name ||
    user?.organizationName ||
    (isOrganizationAdmin
      ? tr("organizationWorkspace", "Espace organisation")
      : "");

  return (
    <nav className="sticky top-0 z-50 border-b border-slate-200 bg-white shadow-sm">
      <div className="mx-auto flex min-h-[72px] max-w-[1500px] items-center gap-4 px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          aria-label="CertiLink"
          className="flex shrink-0 items-center gap-3 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <img src="/logo.jpeg" alt="CertiLink Logo" className="h-full w-full object-contain p-1" />
          </span>
          <span className="hidden xl:block">
            <span className="block text-base font-black tracking-tight text-slate-950">CertiLink</span>
            <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
              {organizationName || tr("digitalCertification", "Digital certification")}
            </span>
          </span>
        </Link>

        <div className="hidden min-w-0 flex-1 items-center justify-between gap-4 lg:flex">
          <div className="flex items-center gap-1 rounded-2xl border border-slate-200 bg-slate-50 p-1.5">
            {links.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={desktopLinkClass}>
                {item.label}
              </NavLink>
            ))}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={toggleLanguage}
              className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
            >
              <Globe2 className="h-4 w-4" />
              {t?.toggleLabel || "FR / EN"}
            </button>

            {isLoggedIn ? (
              <>
                {isActiveClient && canManageThemes && (
                  <Link
                    to="/themes/manage"
                    className="inline-flex h-10 items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 text-sm font-bold text-blue-800 transition hover:bg-blue-100"
                  >
                    <BookOpenCheck className="h-4 w-4" />
                    {tr("manageThemes", "Gérer les thèmes")}
                  </Link>
                )}

                {isActiveClient && (
                  <>
                    <Link
                      to="/certificates/bulk"
                      className="hidden h-10 items-center gap-2 rounded-xl border border-cyan-200 bg-cyan-50 px-3 text-sm font-bold text-cyan-800 transition hover:bg-cyan-100 xl:inline-flex"
                    >
                      <FileStack className="h-4 w-4" />
                      {t?.multiCreate || "Création multiple"}
                    </Link>
                    <Link
                      to="/add-participant"
                      className="inline-flex h-10 items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-4 text-sm font-bold text-white shadow-lg shadow-blue-600/20 transition hover:-translate-y-0.5"
                    >
                      <UserPlus className="h-4 w-4" />
                      {t?.addParticipant || "Ajouter"}
                    </Link>
                  </>
                )}

                {canConfigureCertificateTemplate && (
                  <NavLink
                    to="/organization/certificate-template"
                    className={({ isActive }) =>
                      `inline-flex h-10 items-center gap-2 rounded-xl border px-3 text-sm font-bold transition ${
                        isActive
                          ? "border-violet-300 bg-violet-100 text-violet-950"
                          : "border-violet-200 bg-violet-50 text-violet-800 hover:bg-violet-100"
                      }`
                    }
                    title={
                      lang === "en"
                        ? "Configure certificate template"
                        : "Configurer le modèle de certificat"
                    }
                  >
                    <Settings2 className="h-4 w-4" />
                    <span className="hidden 2xl:inline">
                      {tr(
                        "configureCertificateTemplate",
                        lang === "en"
                          ? "Certificate template"
                          : "Modèle de certificat"
                      )}
                    </span>
                    <span className="2xl:hidden">
                      {lang === "en" ? "PDF template" : "Modèle PDF"}
                    </span>
                  </NavLink>
                )}

                {isAdmin && (
                  <NavLink
                    to="/dashboard"
                    className={({ isActive }) =>
                      `inline-flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-bold text-white shadow-lg transition ${
                        isActive
                          ? "bg-blue-700 shadow-blue-700/25"
                          : "bg-slate-950 shadow-slate-950/20 hover:bg-blue-900"
                      }`
                    }
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    {t?.dashboard || "Dashboard"}
                  </NavLink>
                )}

                <div ref={profileRef} className="relative">
                  <button
                    type="button"
                    aria-expanded={profileOpen}
                    onClick={() => setProfileOpen((current) => !current)}
                    className="flex h-11 items-center gap-2 rounded-2xl border border-slate-200 bg-white p-1.5 pr-3 shadow-sm transition hover:border-blue-200 hover:bg-slate-50"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 text-sm font-black text-white">
                      {userInitial}
                    </span>
                    <span className="hidden max-w-[120px] truncate text-xs font-bold text-slate-900 2xl:block">
                      {user?.name}
                    </span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition ${profileOpen ? "rotate-180" : ""}`} />
                  </button>

                  {profileOpen && (
                    <div className="absolute right-0 top-full mt-3 w-80 overflow-hidden rounded-3xl border border-slate-200 bg-white p-2 shadow-2xl shadow-slate-900/15">
                      <div className="rounded-2xl bg-slate-950 p-4 text-white">
                        <div className="flex items-center gap-3">
                          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600 text-lg font-black">
                            {userInitial}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-bold">{user?.name}</p>
                            <p className="mt-1 text-xs text-slate-300">{roleLabel}</p>
                            {organizationName && (
                              <p className="mt-1 truncate text-xs font-bold text-cyan-300">{organizationName}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="mt-2 flex w-full items-center justify-between rounded-2xl px-4 py-3 text-sm font-bold text-rose-600 transition hover:bg-rose-50"
                      >
                        {t?.logout || "Déconnexion"}
                        <LogOut className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition hover:border-blue-200 hover:bg-blue-50"
                >
                  <LogIn className="h-4 w-4" />
                  {t?.login || "Connexion"}
                </Link>
                <Link
                  to="/requests/create"
                  className="inline-flex h-10 items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-4 text-sm font-bold text-white shadow-lg shadow-blue-600/20 transition hover:-translate-y-0.5"
                >
                  <Send className="h-4 w-4" />
                  {t?.requestAccess || "Demander un accès"}
                </Link>
              </>
            )}
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2 lg:hidden">
          <button
            type="button"
            onClick={toggleLanguage}
            className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700"
          >
            <Globe2 className="h-4 w-4" />
            <span className="hidden sm:inline">{t?.toggleLabel || "FR / EN"}</span>
          </button>
          <button
            type="button"
            aria-label={tr("openMenu", "Ouvrir le menu")}
            aria-expanded={isOpen}
            onClick={() => setIsOpen(true)}
            className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-950 text-white shadow-lg"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="fixed inset-0 z-[80] flex flex-col bg-slate-50 lg:hidden">
          <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-4 shadow-sm">
            <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <img src="/logo.jpeg" alt="CertiLink Logo" className="h-full w-full object-contain p-1" />
              </span>
              <span>
                <span className="block text-base font-black text-slate-950">CertiLink</span>
                <span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">
                  {organizationName || tr("navigation", "Navigation")}
                </span>
              </span>
            </Link>
            <button
              type="button"
              aria-label={tr("closeMenu", "Fermer le menu")}
              onClick={() => setIsOpen(false)}
              className="flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-slate-100 text-slate-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-5">
            {isLoggedIn && user && (
              <div className="mb-5 rounded-3xl bg-slate-950 p-5 text-white shadow-xl">
                <div className="flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 text-lg font-black">
                    {userInitial}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold">{user.name}</p>
                    <p className="mt-1 text-xs text-slate-300">{roleLabel}</p>
                    {organizationName && (
                      <p className="mt-1 truncate text-xs font-bold text-cyan-300">{organizationName}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <p className="mb-2 px-1 text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
              {tr("navigation", "Navigation")}
            </p>
            <div className="space-y-2">
              {links.map(({ to, label, icon: Icon, end }) => (
                <NavLink key={to} to={to} end={end} className={mobileLinkClass}>
                  <Icon className="h-5 w-5" />
                  <span>{label}</span>
                </NavLink>
              ))}
            </div>

            {isLoggedIn && (
              <div className="mt-6">
                <p className="mb-2 px-1 text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
                  {tr("actions", "Actions")}
                </p>
                <div className="space-y-2">
                  {isActiveClient && (
                    <>
                      <Link
                        to="/add-participant"
                        className="flex items-center gap-3 rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-600 px-4 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-600/20"
                      >
                        <UserPlus className="h-5 w-5" />
                        {t?.addParticipant || "Ajouter un participant"}
                      </Link>
                      <Link
                        to="/certificates/bulk"
                        className="flex items-center gap-3 rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-3.5 text-sm font-bold text-cyan-800"
                      >
                        <FileStack className="h-5 w-5" />
                        {t?.multiCreate || "Création multiple"}
                      </Link>
                    </>
                  )}

                  {isActiveClient && canManageThemes && (
                    <Link
                      to="/themes/manage"
                      className="flex items-center gap-3 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3.5 text-sm font-bold text-blue-800"
                    >
                      <BookOpenCheck className="h-5 w-5" />
                      {tr("manageThemes", "Gérer les thèmes")}
                    </Link>
                  )}

                  {canConfigureCertificateTemplate && (
                    <NavLink
                      to="/organization/certificate-template"
                      className={({ isActive }) =>
                        `flex items-center gap-3 rounded-2xl border px-4 py-3.5 text-sm font-bold ${
                          isActive
                            ? "border-violet-600 bg-violet-600 text-white shadow-lg shadow-violet-600/20"
                            : "border-violet-200 bg-violet-50 text-violet-800"
                        }`
                      }
                    >
                      <Settings2 className="h-5 w-5" />
                      {tr(
                        "configureCertificateTemplate",
                        lang === "en"
                          ? "Configure certificate template"
                          : "Configurer le modèle de certificat"
                      )}
                    </NavLink>
                  )}

                  {isAdmin && (
                    <Link
                      to="/dashboard"
                      className="flex items-center gap-3 rounded-2xl bg-slate-950 px-4 py-3.5 text-sm font-bold text-white"
                    >
                      {isSuperAdmin ? <ShieldCheck className="h-5 w-5" /> : <LayoutDashboard className="h-5 w-5" />}
                      {t?.dashboard || "Dashboard"}
                    </Link>
                  )}
                </div>
              </div>
            )}

            <div className="mt-6 border-t border-slate-200 pt-5">
              {isLoggedIn ? (
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-sm font-bold text-rose-700"
                >
                  <LogOut className="h-5 w-5" />
                  {t?.logout || "Déconnexion"}
                </button>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  <Link
                    to="/login"
                    className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3.5 text-sm font-bold text-slate-700"
                  >
                    <LogIn className="h-5 w-5" />
                    {t?.login || "Connexion"}
                  </Link>
                  <Link
                    to="/requests/create"
                    className="flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3.5 text-sm font-bold text-white"
                  >
                    <Send className="h-5 w-5" />
                    {t?.requestAccess || "Demander un accès"}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
